import Cookies from "js-cookie";
import { AuthContext } from "context/AuthContext";
import { SignalContext } from "context/SignalContext";
import { DB_STORE } from "lib/constants";
import { getTokenByActionAndResourceId } from "lib/utils";
import { useContext } from "react";
import { EventWebrtcAction, ResDeviceType } from "webrtc-player/common/base";
import { SinglePcManagerContext } from "context/SinglePcManagerContext";
import {
  fetchPostWithParams,
  fetchWithParams,
  sendHttpRequest,
} from "lib/fetcher";

export function getAlertByGpt(
  text,
  imgS3Key,
  relevanceThreshold = undefined,
  withLocation = false,
  filters: any = {},
  abortController = null,
  version,
  duplicationKey = "",
) {
  const _modifiedFilter = {
    ...filters,
    startedAt: filters?.startedAt ? +filters?.startedAt : undefined,
    endedAt: filters?.endedAt ? +filters?.endedAt : undefined,
    version: imgS3Key || version === "magicV2" ? "image_v1" : undefined,
  };
  if (version === "magicV2") {
    relevanceThreshold = -0.6;
  }

  return sendHttpRequest({
    url: "/alert/alerts/search",
    method: "POST",
    data: {
      text,
      imageUrl: imgS3Key,
      snapshot_req: {
        width: 1024,
      },
      paging: {
        limit: 20,
      },
      threshold: relevanceThreshold,
      withLocation,
      ..._modifiedFilter,
    },
    signal: abortController?.signal,
    duplicationKey,
  });
}
export const changeAlertStatus = async (
  orgId: string,
  alertId: string,
  newStatus: string,
  tag: string,
  desc: string,
) => {
  const token = Cookies.get("token");

  try {
    const data = await fetchPostWithParams(
      `/alert/alerts/${alertId}/change_status`,
      token,
      orgId,
      {
        status: newStatus,
        tag: {
          name: tag,
          desc: desc,
        },
      },
    );
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const reviewAlert = async (orgId: string, alertId: string) => {
  const token = Cookies.get("token");

  try {
    const data = await fetchPostWithParams(
      `/alert/alerts/${alertId}/review`,
      token,
      orgId,
      {},
    );
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export async function getAlertStatusCount(orgId, status: string) {
  const token = Cookies.get("token");

  try {
    const data = await fetchWithParams(
      `/alert/alerts/status/count?status=${status}`,
      token,
      orgId,
    );
    return data;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

export function useService() {
  const { getController } = useContext(SinglePcManagerContext);
  const { getDb } = useContext(SignalContext);
  const { user } = useContext(AuthContext);
  const getCachedImg = async (eventId, mediaId) => {
    try {
      const db = await getDb();
      const dbData = await db.read(DB_STORE.EVENT_PIC, `${eventId}-${mediaId}`);
      if (dbData?.blobData) {
        return dbData?.blobData;
      }
    } catch (error) {
      console.error("getCachedImg", error);
    }

    return null;
  };
  const setCachedImg = async (eventId, mediaId, blobData) => {
    try {
      const db = await getDb();
      try {
        db.put(DB_STORE.EVENT_PIC, {
          id: `${eventId}-${mediaId}`,
          blobData: blobData,
        });
      } catch (error) {
        console.log("Fail to put event pic to db", error);
      }
    } catch (error) {
      console.error("fail to put db", error);
    }
  };
  const getLocalImg = async (alertObj, mediaId, otherObj) => {
    if (mediaId === undefined || mediaId === null) {
      return null;
    }
    const cachedData = await getCachedImg(alertObj.eventId, mediaId);
    if (cachedData) {
      return cachedData;
    }

    const singlePcController = await getController(
      alertObj.nvrId,
      alertObj.deviceId,
      alertObj?.orgId,
    );
    const tokenData = await getTokenByActionAndResourceId(
      [EventWebrtcAction.GetMedia],
      alertObj.eventId,
      user,
      ResDeviceType.EVENT,
      null,
      otherObj,
    );
    return new Promise((resolve, reject) => {
      singlePcController.getMediaImg(
        {
          token: tokenData?.[0].token,
          eventId: alertObj.eventId,
          mediaId: mediaId,
          camera: alertObj.camera,
          speed: 25,
        },
        (err, data) => {
          if (err) {
            reject(err);
            return;
          }
          if (data) {
            const blobData = new Blob([data.obj.binary], {
              type: "image/jpeg",
            });
            setCachedImg(alertObj.eventId, mediaId, blobData);
            resolve(blobData);
          }
        },
      );
    });
  };
  // otherObj仅用于获取case对应的media token
  const getTargetImgByAlertObj = async (alertObj, otherObj?: any) => {
    if (alertObj?.snapshot?.location === "local") {
      return getLocalImg(alertObj, alertObj.targetImages?.[0]?.id, otherObj);
    } else {
      if (!alertObj.targetImages?.[0]?.url) {
        return null;
      }
      const cachedData = await getCachedImg(
        alertObj.eventId,
        alertObj.targetImages?.[0]?.id,
      );
      if (cachedData) {
        return cachedData;
      }
      const response = await fetch(alertObj.targetImages?.[0]?.url);
      console.log("response target", alertObj.targetImages?.[0]?.url);
      const blob = await response.blob();
      setCachedImg(alertObj.eventId, alertObj.targetImages?.[0]?.id, blob);
      return blob;
    }
  };
  const getSnapShotByAlertObj = async (alertObj, otherObj?: any) => {
    if (alertObj?.snapshot?.location === "local") {
      return getLocalImg(alertObj, alertObj.snapshot?.id, otherObj);
    } else {
      const cachedData = await getCachedImg(
        alertObj.eventId,
        alertObj.snapshot?.id,
      );
      if (cachedData) {
        return cachedData;
      }
      console.log("alertObj.snapshot.url", alertObj.snapshot.url);
      const response = await fetch(alertObj.snapshot.url);
      const blob = await response.blob();
      setCachedImg(alertObj.eventId, alertObj.snapshot?.id, blob);
      return blob;
    }
  };
  return {
    getSnapShotByAlertObj,
    getTargetImgByAlertObj,
    getCachedImg,
    setCachedImg,
  };
}
export function requestLicense(cameraId, siteId) {
  return sendHttpRequest({
    url: "/license/request",
    method: "POST",
    data: {
      cameraId,
      siteId,
    },
  });
}
export function getDealerInfo() {
  return sendHttpRequest({
    url: "/org/dealer",
    method: "GET",
  });
}
